import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PRODUCT_PAGE } from "@/constants/testIds";
import { addToCart, getCartCount } from "@/utils/cart";
import { stockModeOf, variantStockMode, comboKey, useDispatchTexts } from "@/utils/stockDispatch";
import {
  Search, Heart, ShoppingCart, User, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Star, Truck, RotateCcw, Package, Headphones, Shield, MapPin, Plus, Minus,
  Share2, Eye, Tag, Clock, CheckCircle, ExternalLink, Menu, CreditCard, Gift, Zap,
  Award, Lock, Download, X, ThumbsUp, ThumbsDown, MoreVertical, Info, Play, XCircle,
} from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { ShopByPriceSection } from "@/pages/home/HomeSections";
import { useShopByPriceConfig } from "@/hooks/useShopByPriceConfig";
import { referralAmount, referralLabel } from "@/utils/referralFee";
import { calcSP } from "@/components/tabs/SuggestiveSPTab";
import { sanitizeHtml } from "@/components/tabs/ContentTab";
import PlanEnrollModal from "@/pages/account/PlanEnrollModal";
import { useStoreSettings } from "@/hooks/useStoreSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_MAP = {
  Yellow: "#FCD34D", White: "#F8FAFC", Pink: "#FCA5A5", Blue: "#93C5FD",
  Green: "#6EE7B7", Red: "#F87171", Black: "#1F2937", Grey: "#9CA3AF",
  Purple: "#C4B5FD", Orange: "#FB923C",
};

const CATEGORIES = [
  "Electronics", "Fashion", "Home & Living", "Sports", "Beauty",
  "Toys & Kids", "Automotive", "Books", "Grocery", "Health",
];

function StarRating({ rating, count, onCountClick }) {
  return (
    <div className="flex items-center gap-1.5" data-testid={PRODUCT_PAGE.ratingStars}>
      <div className="flex">
        {[1, 2, 3, 4, 5].map(i => (
          <span key={i} className={i <= Math.round(rating) ? "star-full text-lg" : "star-empty text-lg"}>★</span>
        ))}
      </div>
      {rating > 0 && <span className="text-sm font-semibold text-slate-700">{rating.toFixed(1)}</span>}
      {count > 0 && (
        <span
          onClick={onCountClick}
          className="text-sm text-blue-600 hover:underline cursor-pointer"
          data-testid="review-count-link"
        >
          ({count.toLocaleString()})
        </span>
      )}
    </div>
  );
}

function CountdownTimer({ hours = 2 }) {
  const [time, setTime] = useState({ h: hours, m: 14, s: 36 });
  useEffect(() => {
    const t = setInterval(() => {
      setTime(prev => {
        let { h, m, s } = prev;
        s--;
        if (s < 0) { s = 59; m--; }
        if (m < 0) { m = 59; h--; }
        if (h < 0) return { h: 23, m: 59, s: 59 };
        return { h, m, s };
      });
    }, 1000);
    return () => clearInterval(t);
  }, []);
  const pad = n => String(n).padStart(2, "0");
  return (
    <span className="font-mono font-bold text-slate-800 text-sm tracking-widest">
      {pad(time.h)} : {pad(time.m)} : {pad(time.s)}
    </span>
  );
}

const MP_META = {
  amazon:  { bg: "#FF9900", text: "#000", initial: "a" },
  flipkart:{ bg: "#2874F0", text: "#fff", initial: "F" },
  meesho:  { bg: "#9B2B8A", text: "#fff", initial: "M" },
  myntra:  { bg: "#FF3F6C", text: "#fff", initial: "M" },
  shopify: { bg: "#95BF47", text: "#fff", initial: "S" },
};

const MP_LOGOS = {
  amazon:   "https://customer-assets-jt897jd0.emergentagent.net/job_webstore-preview/artifacts/5f4dd04a0d1ca07a_amazon.png",
  flipkart: "https://customer-assets-jt897jd0.emergentagent.net/job_webstore-preview/artifacts/2c532fa6b2bf38e1_flipkart.jpg",
  myntra:   "https://customer-assets-jt897jd0.emergentagent.net/job_webstore-preview/artifacts/0cb5e13c7459bc95_myntra.webp",
  shopify:  "https://customer-assets-jt897jd0.emergentagent.net/job_webstore-preview/artifacts/ea783a45bce27d7d_shopify.webp",
};

function MarketplaceIcon({ id, size = 36 }) {
  const logo = MP_LOGOS[id];
  const [err, setErr] = useState(false);
  if (logo && !err) {
    return (
      <div
        className="flex-shrink-0 rounded-lg overflow-hidden flex items-center justify-center bg-white"
        style={{ width: size, height: size }}
      >
        <img
          src={logo}
          alt={id}
          className="w-full h-full object-contain"
          onError={() => setErr(true)}
        />
      </div>
    );
  }
  // Fallback: colored initial circle
  const m = MP_META[id] || { bg: "#64748b", text: "#fff", initial: id?.[0]?.toUpperCase() || "?" };
  return (
    <div
      style={{ width: size, height: size, background: m.bg, borderRadius: 8, flexShrink: 0 }}
      className="flex items-center justify-center shadow-sm"
    >
      <span style={{ color: m.text, fontSize: size * 0.46, fontWeight: 700, lineHeight: 1, fontFamily: "serif" }}>
        {m.initial}
      </span>
    </div>
  );
}

const DEFAULT_TOP_STRIP_ITEMS = ["Free Shipping Pan India", "COD Available", "Easy Returns", "Secure Payments"];

// profit = the same figure shown on the card; falls back to the saved net_profit
function MarketplacePopover({ id, sp, profit, onClose, openUpward = false }) {
  const d = sp[id] || {};
  const rows = id === "amazon" ? [
    { label: "Selling Price",       val: d.item_price },
    { label: referralLabel(d),      val: -referralAmount(d) },
    { label: "Closing Fee",         val: -(d.closing_fee || 0) },
    { label: "Fulfilment Cost",     val: -(d.fulfilment_cost || 0) },
    { label: "Taxes on Fees",       val: -(referralAmount(d) * ((d.tax_on_fees_pct || 0) / 100)) },
    { label: "Product Tax",         val: -(d.product_tax || 0) },
    { label: "Cost per unit",       val: -(d.product_cost || 0) },
    { label: "Net Profit",          val: profit ?? d.net_profit, isProfit: true },
  ] : id === "flipkart" ? [
    { label: "Listing Price",       val: d.listing_price },
    { label: "Seller Price",        val: d.seller_price },
    { label: "Fees and Taxes",      val: -(d.fees_and_taxes || 0) },
    { label: "Bank Settlement",     val: d.bank_settlement || 0 },
    { label: "Logistics Fees",      val: -(d.logistics_fees || 0) },
    { label: "Net Profit",          val: profit ?? d.net_profit, isProfit: true },
  ] : id === "myntra" ? [
    { label: "Selling Price",       val: d.selling_price },
    { label: "Seller Price",        val: d.seller_price || 0 },
    { label: "Pick & Pack Fee",     val: -(d.pick_pack_fee || 0) },
    { label: "Fixed Fee",           val: -(d.fixed_fee || 0) },
    { label: "Commission",          val: -(d.commission || 0) },
    { label: "Collection Fee",      val: -(d.collection_fee || 0) },
    { label: "GT Charge",           val: -(d.gt_charge || 0) },
    { label: "Current Settlement",  val: d.current_settlement || 0 },
    { label: "Net Profit",          val: profit ?? d.net_profit, isProfit: true },
  ] : id === "shopify" ? [
    { label: "Selling Price",       val: d.selling_price },
    { label: "Cost Price",          val: -(d.cost_price || 0) },
    { label: "Tax",                 val: -(d.shopify_tax || 0) },
    { label: "Customer Acquisition Cost", val: -(d.customer_acquisition_cost || 0) },
    { label: "Net Profit",          val: profit ?? d.net_profit, isProfit: true },
  ] : [
    { label: "Item Price",     val: d.item_price },
    { label: "Net Profit",     val: profit ?? d.net_profit, isProfit: true },
  ];

  const label = id.charAt(0).toUpperCase() + id.slice(1);

  // Rendered in a portal with fixed positioning so the surrounding card's
  // overflow-hidden can't clip it; positioned against the trigger's wrapper.
  const anchorRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const place = () => {
      const host = anchorRef.current?.parentElement;
      // Mobile and desktop copies share the open state; keep the hidden copy's popover hidden
      if (!host || host.getClientRects().length === 0) { setPos(null); return; }
      const r = host.getBoundingClientRect();
      const h = popRef.current?.offsetHeight || 0;
      const w = popRef.current?.offsetWidth || 256;
      const spaceBelow = window.innerHeight - r.bottom;
      const up = (openUpward && r.top >= h + 4) || (spaceBelow < h + 4 && r.top > spaceBelow);
      const top = up ? r.top - h - 4 : r.bottom + 4;
      const left = Math.min(Math.max(8, r.right - w), window.innerWidth - w - 8);
      setPos({ top: Math.max(8, top), left });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [openUpward]);

  return (
    <>
    <span ref={anchorRef} className="hidden" />
    {createPortal(
    <div
      ref={popRef}
      style={{ position: "fixed", top: pos?.top ?? 0, left: pos?.left ?? 0, visibility: pos ? "visible" : "hidden" }}
      className="z-50 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
      data-testid={`mp-popover-${id}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 bg-slate-50">
        <MarketplaceIcon id={id} size={22} />
        <span className="text-sm font-semibold text-slate-800">{label} — SP Breakdown</span>
      </div>
      {/* Rows */}
      <div className="px-3 py-2 divide-y divide-slate-50">
        {rows.map((r, i) => (
          <div key={i} className="flex justify-between items-center py-1.5">
            <span className="text-xs text-slate-500">{r.label}</span>
            <span className={`text-xs font-semibold ${
              r.isProfit
                ? (parseFloat(r.val || 0) >= 0 ? "text-green-600" : "text-red-500")
                : parseFloat(r.val || 0) < 0 ? "text-red-400" : "text-slate-800"
            }`}>
              {r.val != null ? `₹${parseFloat(r.val || 0).toFixed(2)}` : "—"}
            </span>
          </div>
        ))}
      </div>
    </div>,
    document.body
    )}
    </>
  );
}



// ─── Auto Review Generator ───────────────────────────────────────────────────
export function generateReviews(product, displayCount = 10) {
  const total = product.num_reviews || 0;
  if (!total) return [];
  const name = (product.item_name || "product").toLowerCase();
  let s = name.split("").reduce((a, c) => a + c.charCodeAt(0), 0) + 42;
  const rng = () => { s = (s * 9301 + 49297) % 233280; return s / 233280; };
  const ri  = (lo, hi) => Math.floor(rng() * (hi - lo + 1)) + lo;
  const pick = arr => arr[ri(0, arr.length - 1)];

  const NAMES = [
    "Priya Sharma","Rahul Gupta","Anjali Singh","Vikram Patel","Sunita Rao",
    "Amit Kumar","Deepa Nair","Rajan Mehta","Kavita Joshi","Suresh Verma",
    "Meena Krishnan","Arjun Das","Neha Agarwal","Rohit Malhotra","Divya Pillai",
    "Sanjay Desai","Pooja Tiwari","Manish Bose","Anita Chowdhury","Kiran Reddy",
    "Harish Iyer","Smitha K","Archana Rani","Jibesh Chandra Das","Bharat Madan",
    "Hiral Jani","Akshara Rajan","Babita Yadav","Gaurav Mishra","Inzmam Ansari",
  ];
  const CITIES = [
    "Mumbai","Delhi","Bangalore","Chennai","Hyderabad","Kolkata","Pune","Ahmedabad",
    "Jaipur","Surat","Lucknow","Nagpur","Indore","Bhopal","Visakhapatnam",
    "Patna","Vadodara","Agra","Nashik","Faridabad","Meerut","Rajkot","Amritsar",
    "Bidhan Nagar","New Delhi","Chhindwara","Varanasi","Kanpur","Coimbatore","Kochi",
  ];
  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

  const isClothing = /underwear|brief|boxer|shirt|top|dress|kurti|saree|wear|cloth/i.test(name);
  const isJewelry  = /ring|necklace|bracelet|earring|bangle|jewel|pendant/i.test(name);

  const TITLES = isClothing
    ? ["Very comfortable!","Great quality fabric","Perfect fit","Excellent product","Love it!",
       "Worth every penny","Good for daily wear","Exactly as described","Great value","Satisfied",
       "Brilliant","Simply amazing","Terrific","Super!","Nice product","Good buy"]
    : isJewelry
    ? ["Beautiful piece","Elegant design","Great quality","Loved it!","Perfect gift",
       "Stunning product","Very beautiful","Excellent quality","Worth buying","Great purchase",
       "Brilliant","Lovely","Terrific purchase","Super quality","Nice design","Value-for-money"]
    : ["Great product","Excellent quality","Very happy","Good value","Recommended",
       "Worth buying","Nice product","Satisfied customer","Good purchase","Great deal",
       "Brilliant","Simply amazing","Terrific purchase","Super!","Value-for-money","Nice quality"];

  const BODIES = isClothing
    ? [
        "Very comfortable material, perfect for everyday wear. Fits true to size.",
        "The fabric quality is excellent. Very soft and comfortable throughout the day.",
        "Great product! The stitching is very neat and the material is of top quality.",
        "Exactly as shown in the photos. Comfortable fit and good quality fabric.",
        "Very happy with this purchase. Highly recommend to anyone looking for quality innerwear.",
        "Good quality product. The material feels premium. Worth every rupee.",
        "Fits perfectly. Comfortable for long hours. Will definitely buy more.",
        "Amazing quality for the price. The product looks exactly like the pictures.",
        "Very satisfied with the product. Comfortable and durable. Great purchase.",
        "The quality exceeded my expectations. Very comfortable and true to size.",
        "Powerbackup is good. Design is portable. Overall very good product.",
        "Ease of charging because it's not too heavy and gives a good experience overall.",
        "Good product, simple and elegant. Worth purchasing for everyday use.",
        "Nice product. Delivery was fast and packaging was excellent. Highly recommend.",
      ]
    : isJewelry
    ? [
        "Beautiful design and excellent finish. Got many compliments wearing it.",
        "The quality is amazing. Looks exactly like the picture and very elegant.",
        "Lovely piece of jewellery. Good packaging, arrived safely. Very happy!",
        "Great quality for the price. Looks premium and goes with everything.",
        "Beautiful product. My wife loved it as a gift. Excellent quality.",
        "Stunning design. The finish is very nice. Worth every penny.",
        "Very happy with this purchase. Looks just like the photos, maybe even better!",
        "Good quality metal and stone. No discoloration even after weeks of use.",
        "The packaging was beautiful and product quality is amazing. 5 stars!",
        "Great product! Very satisfied. Will order again and recommend to friends.",
        "Just your product being start use, it is not easy to detect quality in short period. After long time of use it is to be ascertain the quality of the product!",
        "This is a game-changer. Its sleek design makes it effortlessly portable, fitting snugly and the quality is superb.",
      ]
    : [
        "Great product, exactly as described. Very happy with the quality.",
        "Good quality, arrived quickly. Packaging was excellent. Recommended!",
        "Very satisfied with this purchase. The quality is very good.",
        "Exactly as shown. Good build quality and worth the price.",
        "Excellent product! Great value for money. Will definitely buy again.",
        "Happy with the purchase. Good quality and fast delivery.",
        "The quality is better than expected. Very satisfied customer.",
        "Good product. Recommended for anyone looking for good quality.",
        "Very good quality for the price. Will recommend to friends.",
        "Satisfied with the product. Works as described. Good purchase.",
        "Just your product being start use, it is not easy to detect quality in short period. After long time of use it is to be ascertain the quality of the product!",
        "This is a game-changer. Its sleek design makes it effortlessly portable, fitting snugly into pockets or bags without adding any bulk. Nice quality and fast charging works great.",
      ];

  const weightedStar = () => {
    const t = ri(1, 100);
    if (t <= 2) return 1;
    if (t <= 7) return 2;
    if (t <= 15) return 3;
    if (t <= 35) return 4;
    return 5;
  };

  // Collect variant info for "Review for:" label
  const colors = [...new Set((product.variant_combinations || []).map(v => v.color).filter(Boolean))];
  const sizes  = [...new Set((product.variant_combinations || []).map(v => v.size).filter(Boolean))];

  // Gather product images
  const allImages = [
    ...(product.main_image ? [product.main_image] : []),
    ...(product.gallery_images || []).filter(Boolean),
    ...(product.variant_combinations || []).map(v => v.image).filter(Boolean),
  ].filter((v, i, a) => a.indexOf(v) === i).slice(0, 10);

  const N = Math.min(total, displayCount);
  return Array.from({ length: N }, (_, i) => {
    const stars    = weightedStar();
    const hasImg   = rng() < 0.3 && allImages.length > 0;
    const imgCount = hasImg ? (rng() > 0.6 ? 2 : 1) : 0;
    const imgs     = hasImg ? Array.from({ length: imgCount }, (__, j) => allImages[(i + j) % allImages.length]) : [];
    const m        = ri(0, 11), y = rng() > 0.5 ? 2025 : 2024;
    const color    = colors.length ? pick(colors) : null;
    const size     = sizes.length  ? pick(sizes)  : null;
    const variantInfo = [color && `Color ${color}`, size && `Size ${size}`].filter(Boolean).join(" • ");
    return {
      id: i,
      name: NAMES[i % NAMES.length],
      city: CITIES[ri(0, CITIES.length - 1)],
      stars,
      title: pick(TITLES),
      body: pick(BODIES),
      monthYear: `${MONTHS[m]}, ${y}`,
      verified: rng() > 0.08,
      helpful: ri(0, 21),
      thumbsDown: ri(0, 4),
      images: imgs,
      variantInfo,
    };
  });
}

// ─────────────────────────────────────────────────────────────────────────────

// Product video: YouTube / Vimeo -> embed, direct mp4/webm/ogv/mov or our own upload -> <video>.
// Anything else returns null so nothing is rendered.
function getProductVideo(url) {
  const u = (url || "").trim();
  if (!/^https?:\/\//i.test(u)) return null;
  const yt = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/i);
  if (yt) return { type: "embed", src: `https://www.youtube.com/embed/${yt[1]}` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vm) return { type: "embed", src: `https://player.vimeo.com/video/${vm[1]}` };
  if (/\.(mp4|webm|ogv|mov)(\?.*)?$/i.test(u) || u.includes("/api/images/")) return { type: "file", src: u };
  return null;
}

function ProductVideoPlayer({ video, className = "w-full h-full" }) {
  return video.type === "embed" ? (
    <iframe
      src={video.src}
      title="Product video"
      className={className}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  ) : (
    <video src={video.src} controls playsInline preload="metadata" className={`${className} object-contain`} />
  );
}

export default function ProductPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const storeSettings = useStoreSettings();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);
  const mobileGalleryRef = useRef(null);
  const [galleryColor, setGalleryColor] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [activeTab, setActiveTab] = useState("description");
  const [cartCount, setCartCount] = useState(() => getCartCount());
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [couponSaved, setCouponSaved] = useState(false);
  const [stockNote, setStockNote] = useState("");
  const dispatchTexts = useDispatchTexts();
  const saveCoupon = () => {
    try { localStorage.setItem("it_pending_coupon", product.coupon_code); } catch (e) { /* storage blocked */ }
    setCouponSaved(true);
  };
  const handleShare = async () => {
    const url = window.location.href;
    const title = product?.item_name || document.title;
    try {
      if (navigator.share) { await navigator.share({ title, url }); return; }
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (e) { /* user cancelled the share sheet */ }
  };
  const shopByPriceConfig = useShopByPriceConfig();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMpPopover, setOpenMpPopover] = useState(null);
  const [topStrip, setTopStrip] = useState({ enabled: true, items: DEFAULT_TOP_STRIP_ITEMS });
  const [isTitleExpanded, setIsTitleExpanded] = useState(false);
  const [variantsExpanded, setVariantsExpanded] = useState(false);
  const [offersExpanded, setOffersExpanded] = useState(false);
  const [openOfferPopover, setOpenOfferPopover] = useState(null); // { title, desc, full }
  const [colorsExpanded, setColorsExpanded] = useState(false);
  const [bulkExpanded, setBulkExpanded] = useState(false);
  const [marketplaceExpanded, setMarketplaceExpanded] = useState(true);
  const [highlightsOpen, setHighlightsOpen] = useState(true);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [measurementsOpen, setMeasurementsOpen] = useState(false);
  const [styleOpen, setStyleOpen] = useState(false);
  const [qaOpen, setQaOpen] = useState(true);
  const [stickyEnabled, setStickyEnabled] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const inlineButtonsRef = useRef(null);
  const variantPickerRef = useRef(null);
  const [variantHint, setVariantHint] = useState(false);
  // Add to Cart / Buy Now: with nothing selected, take the shopper to the variant picker and say why
  const handleCta = (dest) => {
    const added = addToCart(product, quantities, currentPrice);
    if (added) { setCartCount(getCartCount()); navigate(dest); return; }
    variantPickerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setVariantHint(true);
    setTimeout(() => setVariantHint(false), 3500);
  };
  const reviewsRef = useRef(null);
  const [settingsDeliveryDays, setSettingsDeliveryDays] = useState(2);
  const [settingsCutoffHour, setSettingsCutoffHour] = useState(18);
  const [deliverTo, setDeliverTo] = useState(() => localStorage.getItem("it_deliver_to") || "");
  const [editingLocation, setEditingLocation] = useState(false);
  const [locationInput, setLocationInput] = useState("");
  const [showSpecsPopover, setShowSpecsPopover] = useState(false);
  const [activeSpecTab, setActiveSpecTab] = useState("specifications");

  // ── Product Data Download state ────────────────────────────────────────────
  const [pdState, setPdState] = useState("idle"); // idle | adding | added | limit
  const [showPlanModal, setShowPlanModal] = useState(false);
  const customerToken = localStorage.getItem("it_customer_token") || "";

  const handleGetProductData = async () => {
    if (!customerToken) { navigate("/customer-login", { state: { redirect: window.location.pathname } }); return; }
    if (pdState === "added") { navigate("/account", { state: { tab: "product-data" } }); return; }
    setPdState("adding");
    try {
      const res = await axios.post(`${API}/customer/product-data`, {
        product_id:    product.product_id || product.id,
        product_title: product.item_name || "",
        product_sku:   product.product_id || "",
        product_url:   window.location.href,
        product_image: product.main_image || "",
      }, { headers: { Authorization: `Bearer ${customerToken}` } });
      if (res.data.already_exists || res.data.added) {
        setPdState("added");
        toast.success("Product data added! Go to Account → Product Data to download.");
      } else if (res.data.limit_reached) {
        setPdState("limit");
        setShowPlanModal(true);
      }
    } catch (e) {
      setPdState("idle");
      toast.error("Failed to add product data. Please try again.");
    }
  };

  useEffect(() => {
    const sync = () => setCartCount(getCartCount());
    window.addEventListener("it_cart_updated", sync);
    return () => window.removeEventListener("it_cart_updated", sync);
  }, []);

  useEffect(() => {
    axios.get(`${API}/products/public/${slug}`)
      .then(res => {
        setProduct(res.data);
        // Pre-select variant from URL param (e.g. ?color=Black) or default to first
        const opts = res.data.variant_options || [];
        const v1 = opts[0];
        if (v1?.values?.length) {
          const keyLower = v1.name.toLowerCase();
          const urlVal = searchParams.get(keyLower);
          const preSelect = (urlVal && v1.values.includes(urlVal)) ? urlVal : v1.values[0];
          setGalleryColor(preSelect);
        }
        setLoading(false);
      })
      .catch(() => {
        setNotFound(true);
        setLoading(false);
      });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch storefront settings
  useEffect(() => {
    axios.get(`${API}/settings`)
      .then(res => {
        setStickyEnabled(res.data?.storefront_sticky_cta ?? true);
        setTopStrip({
          enabled: res.data?.product_top_strip_enabled ?? true,
          items: Array.isArray(res.data?.product_top_strip_items) ? res.data.product_top_strip_items : DEFAULT_TOP_STRIP_ITEMS,
        });
        setSettingsDeliveryDays(res.data?.default_delivery_days ?? 2);
        setSettingsCutoffHour(res.data?.order_cutoff_hour ?? 18);
      })
      .catch(() => setStickyEnabled(true));
  }, []);

  // IntersectionObserver: show sticky bar only when inline buttons leave viewport
  // Depends on `product` so it runs after the product data loads and buttons are rendered
  useEffect(() => {
    if (!product || !inlineButtonsRef.current) return;
    const el = inlineButtonsRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center text-center px-4">
        <Package className="w-16 h-16 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-slate-800 mb-2">Product Not Found</h1>
        <p className="text-slate-500 mb-6">This product may be unavailable or the URL may be incorrect.</p>
        <Link to="/" className="bg-orange-500 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-orange-600 transition-all">
          Go to Dashboard
        </Link>
      </div>
    );
  }

  const allImages = [product.main_image, ...(product.gallery_images || [])].filter(Boolean);
  const colorOpt = product.variant_options?.find(o => o.name?.toLowerCase() === "color");
  const sizeOpt = product.variant_options?.find(o => o.name?.toLowerCase() === "size");
  const colorValues = colorOpt?.values || [];
  const sizeValues = sizeOpt?.values || [];

  // Variant gallery: multi-images[] takes priority, then single image, then product gallery
  const selectedVariantCombo = galleryColor
    ? product.variant_combinations?.find(c => c.color === galleryColor) || null
    : null;

  const variantMultiImages = selectedVariantCombo?.images?.filter(Boolean) || [];
  const variantSingleImg   = selectedVariantCombo?.image || null;

  const displayImages = variantMultiImages.length > 0
    ? variantMultiImages
    : variantSingleImg
      ? [variantSingleImg, ...allImages.filter(img => img !== variantSingleImg)]
      : allImages;

  // Product video (Media tab) is shown as the last item of the image gallery
  const productVideo = getProductVideo(product.video_url);
  const videoIndex = displayImages.length;
  const galleryCount = displayImages.length + (productVideo ? 1 : 0);
  const showingVideo = !!productVideo && activeImg === videoIndex;
  const openVideo = () => {
    setActiveImg(videoIndex);
    const el = mobileGalleryRef.current;
    if (el) {
      // Jump straight to the video slide (smooth programmatic scroll can stall on some phones)
      el.style.scrollBehavior = "auto";
      el.scrollLeft = videoIndex * el.offsetWidth;
      el.style.scrollBehavior = "smooth";
    }
  };

  // All variant combinations for multi-select table
  const allVariants = product.variant_combinations || [];

  // MOQ pricing
  const moqTiers = product.moq_tiers || [];
  const sortedTiers = [...moqTiers].sort((a, b) => Number(a.min_qty) - Number(b.min_qty));

  // Per-variant qty handler
  const handleQtyChange = (key, newQty) => {
    // "Stop at stock" logic is now checked per-variant
    const combo = allVariants.find(c => comboKey(c) === key);
    if (variantStockMode(product, combo) === "strict") {
      if (combo && typeof combo.stock === "number" && newQty > combo.stock) {
        setStockNote(combo.stock > 0 ? `Only ${combo.stock} in stock for ${combo.size || combo.color || "this option"}` : "This option is out of stock");
        newQty = combo.stock;
      } else {
        setStockNote("");
      }
    }
    setQuantities(prev => {
      const updated = { ...prev };
      if (newQty <= 0) delete updated[key];
      else updated[key] = newQty;
      return updated;
    });
  };

  // Aggregate totals across all selected variants
  const totalQty = Object.values(quantities).reduce((sum, q) => sum + q, 0);

  const basePrice = product.deal_price_incl_gst || (moqTiers[0]?.price) || 0;

  // Current unit price from MOQ tiers based on total qty
  const currentPrice = (() => {
    if (sortedTiers.length === 0) return basePrice;
    let price = sortedTiers[0]?.price || basePrice;
    for (const tier of sortedTiers) {
      if (totalQty >= Number(tier.min_qty)) price = tier.price;
    }
    return price;
  })();

  // Marketplace prices from suggestive_sp
  const sp = product.suggestive_sp || {};

  // Replicate admin panel formula: net_profit = item_price - referral - tax_on_referral - fulfilment - other - product_cost
  // Same per-marketplace formulas as the admin Suggestive SP tab
  const calcMpProfit = (mp, id) => (mp ? calcSP(mp, id).net_profit : 0);

  const mpPrices = [
    { id: "amazon",   label: "Amazon",   price: sp.amazon?.item_price || 0,                              profit: calcMpProfit(sp.amazon, "amazon") },
    { id: "flipkart", label: "Flipkart", price: sp.flipkart?.listing_price || sp.flipkart?.item_price || 0, profit: calcMpProfit(sp.flipkart, "flipkart") },
    { id: "meesho",   label: "Meesho",   price: sp.meesho?.item_price || 0,                               profit: calcMpProfit(sp.meesho, "meesho") },
    { id: "myntra",   label: "Myntra",   price: sp.myntra?.selling_price || sp.myntra?.item_price || 0,   profit: calcMpProfit(sp.myntra, "myntra") },
    { id: "shopify",  label: "Shopify",  price: sp.shopify?.selling_price || sp.shopify?.item_price || 0, profit: calcMpProfit(sp.shopify, "shopify") },
  ].filter(m => m.price > 0);

  // Review stats mock
  const totalRev = product.num_reviews || 0;
  const rating = product.star_rating || 0;
  const breakdown = [
    { stars: 5, pct: 62 }, { stars: 4, pct: 18 }, { stars: 3, pct: 10 },
    { stars: 2, pct: 6 }, { stars: 1, pct: 4 }
  ];

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Announcement Bar — enabled + texts come from Admin → Settings */}
      {topStrip.enabled && (
      <div className="bg-slate-900 text-white py-1.5 text-xs" data-testid="product-top-strip">
        <div className="max-w-[1800px] mx-auto px-3 flex items-center justify-between">
          <div className="flex items-center gap-4 overflow-x-auto no-scrollbar">
            {topStrip.items.filter(t => t && t.trim()).map((text, i) => {
              const Icon = /secure/i.test(text) ? Shield : CheckCircle;
              const vis = ["flex", "hidden sm:flex", "hidden md:flex"][i] || "hidden lg:flex";
              return (
                <span key={i} className={`${vis} items-center gap-1 whitespace-nowrap`}><Icon className="w-3 h-3 text-green-400" />{text}</span>
              );
            })}
          </div>
          <div className="flex items-center gap-3 flex-shrink-0 ml-4">
            <span className="text-slate-400 hover:text-white cursor-pointer hidden sm:block">Track Order</span>
            <span className="text-slate-400 hover:text-white cursor-pointer">Help</span>
            <span className="flex items-center gap-1 text-slate-300">🇮🇳 INR</span>
          </div>
        </div>
      </div>
      )}

      <SiteHeader cartCount={cartCount} showNav={true} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-[1800px] mx-auto px-3 py-2">
          <nav className="flex items-center gap-1 text-xs text-slate-500 overflow-x-auto no-scrollbar">
            <Link to="/" className="hover:text-orange-500 whitespace-nowrap">Home</Link>
            {(Array.isArray(product.collection) ? product.collection : [product.collection]).filter(Boolean).slice(0, 2).map((c, i) => (
              <span key={i} className="flex items-center gap-1 whitespace-nowrap">
                <ChevronRight className="w-3 h-3" />
                <Link
                  to={`/c/${c.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}
                  className="hover:text-orange-500 transition-colors"
                >
                  {c}
                </Link>
              </span>
            ))}
            <span className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-800 truncate max-w-48">{product.item_name}</span>
            </span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <div className="max-w-[1800px] mx-auto px-3 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Left: Image Gallery — 6 cols ≈ 50% */}
          <div className="lg:col-span-6 lg:sticky lg:top-28 lg:self-start">
            <div className="flex flex-col gap-2 lg:flex-row">

              {/* Main Image — mobile: swipeable, desktop: static */}
              <div className="flex-1 min-w-0 lg:order-2">

                {/* Mobile swipe gallery */}
                <div className="relative lg:hidden rounded-lg overflow-hidden bg-slate-50 aspect-square">
                  <div
                    ref={mobileGalleryRef}
                    className="flex h-full no-scrollbar"
                    style={{ overflowX: 'scroll', scrollSnapType: 'x mandatory', scrollBehavior: 'smooth', WebkitOverflowScrolling: 'touch' }}
                    onScroll={(e) => {
                      const idx = Math.round(e.currentTarget.scrollLeft / (e.currentTarget.offsetWidth || 1));
                      if (idx !== activeImg) setActiveImg(idx);
                    }}
                  >
                    {(displayImages.length > 0 || productVideo ? displayImages : [null]).map((img, idx) => (
                      <div key={idx} className="flex-shrink-0 w-full h-full flex items-center justify-center" style={{ scrollSnapAlign: 'start' }}>
                        {img ? (
                          <img src={img} alt={`${idx + 1}`} className="w-full h-full object-contain" onError={e => { e.target.style.display = "none"; }} />
                        ) : (
                          <Package className="w-20 h-20 text-slate-300" />
                        )}
                      </div>
                    ))}
                    {productVideo && (
                      <div className="flex-shrink-0 w-full h-full flex items-center justify-center bg-black" style={{ scrollSnapAlign: 'start' }} data-testid="product-video">
                        <ProductVideoPlayer video={productVideo} className="w-full aspect-video" />
                      </div>
                    )}
                  </div>
                  {/* Play button — jumps to the video slide */}
                  {productVideo && !showingVideo && (
                    <button onClick={openVideo} aria-label="Play product video" className="absolute bottom-9 right-3 z-10 w-11 h-11 bg-white shadow-md rounded-full flex items-center justify-center border border-slate-100" data-testid="product-video-play">
                      <Play className="w-5 h-5 text-slate-900 fill-current ml-0.5" />
                    </button>
                  )}
                  {/* Share & Wishlist overlay */}
                  <div className="absolute top-2 right-2 flex gap-1.5 z-10">
                    <button onClick={handleShare} aria-label="Share" title={shareCopied ? "Link copied" : "Share"} className="w-9 h-9 bg-white shadow rounded-full flex items-center justify-center border border-slate-100">
                      <Share2 className={`w-4 h-4 ${shareCopied ? "text-green-600" : "text-slate-600"}`} />
                    </button>
                    <button onClick={() => setWishlist(!wishlist)} className={`w-9 h-9 bg-white shadow rounded-full flex items-center justify-center border transition-all ${wishlist ? "border-red-300 bg-red-50" : "border-slate-100"}`}>
                      <Heart className={`w-4 h-4 ${wishlist ? "text-red-500 fill-current" : "text-slate-600"}`} />
                    </button>
                  </div>
                  {/* Dot indicators */}
                  {galleryCount > 1 && (
                    <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
                      {Array.from({ length: galleryCount }).map((_, idx) => (
                        <div key={idx} className={`w-1.5 h-1.5 rounded-full transition-all ${activeImg === idx ? "bg-orange-500" : "bg-slate-300/80"}`} />
                      ))}
                    </div>
                  )}
                  {/* Counter badge */}
                  {galleryCount > 1 && (
                    <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full">
                      {activeImg + 1}/{galleryCount}
                    </div>
                  )}
                </div>

                {/* Desktop main image */}
                <div className="relative hidden lg:block bg-slate-50 rounded-lg overflow-hidden">
                  <div className="absolute top-2 right-2 flex gap-1.5 z-10">
                    <button onClick={handleShare} aria-label="Share" title={shareCopied ? "Link copied" : "Share"} className="w-9 h-9 bg-white shadow rounded-full flex items-center justify-center hover:bg-slate-100 border border-slate-100 transition-colors">
                      <Share2 className={`w-4 h-4 ${shareCopied ? "text-green-600" : "text-slate-600"}`} />
                    </button>
                    <button onClick={() => setWishlist(!wishlist)} className={`w-9 h-9 bg-white shadow rounded-full flex items-center justify-center border transition-all ${wishlist ? "border-red-300 bg-red-50" : "border-slate-100 hover:bg-slate-100"}`}>
                      <Heart className={`w-4 h-4 ${wishlist ? "text-red-500 fill-current" : "text-slate-600"}`} />
                    </button>
                  </div>
                  <div className="w-full aspect-square flex items-center justify-center">
                    {showingVideo ? (
                      <div className="w-full h-full bg-black flex items-center justify-center" data-testid="product-video">
                        <ProductVideoPlayer video={productVideo} />
                      </div>
                    ) : displayImages.length > 0 ? (
                      <img src={displayImages[activeImg] || displayImages[0]} alt={product.item_name} className="w-full h-full object-contain" onError={e => { e.target.style.display = "none"; }} />
                    ) : (
                      <Package className="w-20 h-20 text-slate-300" />
                    )}
                  </div>
                  {/* Play button — switches the main viewer to the video */}
                  {productVideo && !showingVideo && (
                    <button onClick={openVideo} aria-label="Play product video" className="absolute bottom-4 right-4 z-10 w-12 h-12 bg-white shadow-md rounded-full flex items-center justify-center border border-slate-100 hover:bg-slate-100 transition-colors" data-testid="product-video-play">
                      <Play className="w-5 h-5 text-slate-900 fill-current ml-0.5" />
                    </button>
                  )}
                </div>
              </div>

              {/* Thumbnails — desktop only */}
              {galleryCount > 1 && (
                <div className="hidden lg:flex flex-col gap-1.5 overflow-y-auto no-scrollbar max-h-[560px] lg:order-1 flex-shrink-0">
                  {displayImages.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => setActiveImg(idx)}
                      className={`flex-shrink-0 cursor-pointer rounded-lg overflow-hidden bg-white transition-all ${
                        activeImg === idx ? "border-2 border-orange-500" : "border border-slate-200 hover:border-orange-400"
                      }`}
                      style={{ width: 54, height: 54 }}
                    >
                      <img src={img} alt={`Thumb ${idx + 1}`} className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} />
                    </div>
                  ))}
                  {productVideo && (
                    <div
                      onClick={openVideo}
                      className={`relative flex-shrink-0 cursor-pointer rounded-lg overflow-hidden bg-white transition-all ${
                        showingVideo ? "border-2 border-orange-500" : "border border-slate-200 hover:border-orange-400"
                      }`}
                      style={{ width: 54, height: 54 }}
                      data-testid="product-video-thumb"
                    >
                      {displayImages[0] && (
                        <img src={displayImages[0]} alt="Product video" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <div className="w-7 h-7 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-3.5 h-3.5 text-slate-900 fill-current ml-0.5" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Center: Product Info — 4 cols ≈ 33% */}
          <div className="lg:col-span-4">
            {/* Brand store link */}
            {product.brand_name && (
              <p className="text-xs text-blue-600 hover:underline cursor-pointer mb-0.5">
                Visit the {product.brand_name} Store
              </p>
            )}

            {/* Title — single line with inline chevron toggle */}
            <div className="flex items-center justify-between gap-2 mb-1" data-testid={PRODUCT_PAGE.productTitle}>
              <h1
                className={`text-base font-bold text-slate-900 leading-snug flex-1 min-w-0 ${!isTitleExpanded ? "truncate" : ""}`}
              >
                {product.item_name}
              </h1>
              <button
                onClick={() => setIsTitleExpanded(e => !e)}
                className="flex-shrink-0 text-slate-500 hover:text-slate-700"
                title={isTitleExpanded ? "Show less" : "Show full title"}
              >
                {isTitleExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>

            {/* Rating row */}
            {(product.star_rating > 0 || product.num_reviews > 0) && (
              <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                <StarRating rating={product.star_rating || 0} count={product.num_reviews || 0} onCountClick={() => reviewsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })} />
              </div>
            )}

            {/* Bought in past month */}
            {product.num_reviews > 0 && (
              <p className="text-xs text-slate-500 mb-1.5">
                <strong>{product.num_reviews > 200 ? "200+" : product.num_reviews}</strong> bought in past month
              </p>
            )}
            <hr className="border-slate-200 mb-1.5" />

            {/* ── MOBILE ONLY: Bulk Buying + Marketplace SP above price ── */}
            {sortedTiers.length > 0 && (
              <div className="block lg:hidden border border-slate-200 rounded-xl bg-white overflow-hidden mb-2">
                <div className="flex items-stretch gap-2 p-2">
                  {sortedTiers.slice(0, 3).map((tier, idx) => {
                    const tierMin = Number(tier.min_qty);
                    const nextTier = sortedTiers[idx + 1];
                    const tierMax = nextTier ? Number(nextTier.min_qty) - 1 : Infinity;
                    const isTierActive = totalQty > 0 && totalQty >= tierMin && totalQty <= tierMax;
                    return (
                      <div key={idx} className={`flex-1 rounded-xl px-3 py-2.5 min-w-0 transition-all ${
                        isTierActive ? "border-2 border-orange-400 bg-orange-50" : "border border-slate-200 bg-slate-50/60"
                      }`}>
                        <p className={`text-lg font-bold leading-tight ${isTierActive ? "text-orange-500" : "text-slate-800"}`}>
                          &#8377;{tier.price}
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {idx === 0 ? `Min ${tier.min_qty} pcs` : `≥ ${tier.min_qty} pcs`}
                        </p>
                      </div>
                    );
                  })}
                  {product.bought_past_month > 0 && (
                    <div className="flex items-center justify-center flex-shrink-0 ml-1">
                      <span className="bg-slate-100 text-slate-600 text-xs px-2.5 py-1.5 rounded-full whitespace-nowrap font-medium">
                        {product.bought_past_month.toLocaleString()} sold
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {mpPrices.length > 0 && (
              <div className="block lg:hidden border border-slate-200 rounded-lg overflow-hidden mb-2">
                <button
                  onClick={() => setMarketplaceExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-2.5 py-2 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full border-2 border-slate-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-slate-700 leading-none">SP</span>
                    </div>
                    <span className="text-xs font-bold text-slate-900">Marketplace Suggestive SP</span>
                    <span className="text-xs text-slate-400">({mpPrices.length})</span>
                  </div>
                  {marketplaceExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                </button>
                {marketplaceExpanded && (
                  <div className="border-t border-slate-100 px-2 py-2">
                    <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                      {mpPrices.map((mp, mpIdx) => (
                        <div key={mp.id} className="relative flex-shrink-0">
                          <button
                            onClick={() => setOpenMpPopover(openMpPopover === mp.id ? null : mp.id)}
                            className={`flex flex-col items-center gap-0.5 px-2.5 py-2 rounded-xl border transition-all w-16 ${
                              openMpPopover === mp.id ? "border-blue-400 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <MarketplaceIcon id={mp.id} size={28} />
                            <p className="text-[11px] font-bold text-slate-900 leading-tight mt-0.5">&#8377;{mp.price.toLocaleString()}</p>
                            {mp.profit !== 0 && (
                              <p className={`text-[9px] font-semibold leading-tight ${mp.profit > 0 ? "text-green-600" : "text-red-500"}`}>
                                {mp.profit > 0 ? "+" : ""}&#8377;{Math.round(Math.abs(mp.profit))}
                              </p>
                            )}
                          </button>
                          {openMpPopover === mp.id && (
                            <MarketplacePopover id={mp.id} sp={sp} profit={mp.profit} onClose={() => setOpenMpPopover(null)} openUpward={mpIdx >= mpPrices.length - 2} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Price block */}
            <div className="mb-1.5" data-testid={PRODUCT_PAGE.productPrice}>
              {product.mrp > 0 && (
                <>
                  <div className="flex items-baseline gap-1.5 mb-0.5 flex-wrap">
                    <span className="text-lg font-bold text-red-500">
                      -{Math.round(((product.mrp - (product.deal_price_excl_gst || product.mrp)) / product.mrp) * 100)}%
                    </span>
                    <span className="text-2xl font-bold text-slate-900">&#8377;{Number(product.deal_price_excl_gst || product.mrp).toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-slate-500">M.R.P.: <span className="line-through">&#8377;{Number(product.mrp).toLocaleString()}</span></p>
                </>
              )}
              {!product.mrp && product.deal_price_excl_gst > 0 && (
                <span className="text-2xl font-bold text-slate-900">&#8377;{Number(product.deal_price_excl_gst).toLocaleString()}</span>
              )}
            </div>

            {/* Coupon Discount Strip */}
            {product.coupon_label && (
              <div className="flex items-center gap-2.5 mb-2 pb-2 border-b border-slate-200">
                {/* Amazon-style ticket icon: rectangle with concave notch on right */}
                <svg width="22" height="17" viewBox="0 0 22 17" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                  <path d="M1 1.5 C1 1.2 1.2 1 1.5 1 L15 1 L15 5 Q17.5 5 17.5 8.5 Q17.5 12 15 12 L15 16 L1.5 16 C1.2 16 1 15.8 1 15.5 Z" stroke="#555" strokeWidth="1" fill="none"/>
                  <text x="8" y="11.5" fontSize="7" fontWeight="700" fill="#555" textAnchor="middle" fontFamily="sans-serif">₹</text>
                </svg>
                {/* Text */}
                <p className="text-xs text-slate-900 flex-1 flex items-center gap-1 flex-wrap">
                  <strong>Coupon Discount</strong>
                  <span className="bg-green-200 text-green-800 font-semibold px-1 py-px rounded-sm">
                    Save {product.coupon_label}
                  </span>
                  now.{" "}
                  {product.coupon_details_url && (
                    <a href={product.coupon_details_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Details</a>
                  )}
                </p>
                {/* Pill outline button */}
                {product.coupon_code && (
                  <button onClick={saveCoupon} data-testid="product-coupon-apply" title={couponSaved ? `${product.coupon_code} will be applied at checkout` : `Apply ${product.coupon_code}`} className={`flex-shrink-0 border text-xs font-normal px-2.5 py-1 rounded-full transition-colors whitespace-nowrap ${couponSaved ? "border-green-300 bg-green-50 text-green-700" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
                    {couponSaved ? "Applied ✓ at checkout" : (product.coupon_button_text || "Apply Coupon")}
                  </button>
                )}
              </div>
            )}
            {product.offers && product.offers.trim() && (() => {
              const lines = product.offers.split('\n').filter(o => o.trim()).slice(0, 3);
              if (!lines.length) return null;
              const TITLES = ['Cashback', 'Bank Offer', 'Partner Offers'];
              const KEYWORDS = [
                ['cashback','cash back','upi','paytm','gpay','wallet'],
                ['bank','credit','debit','emi','card'],
                ['gst','invoice','partner','free','ship','deliver','exclusive'],
              ];
              const getTitle = (text, i) => {
                const t = text.toLowerCase();
                for (let j = 0; j < KEYWORDS.length; j++)
                  if (KEYWORDS[j].some(k => t.includes(k))) return TITLES[j];
                return TITLES[i] || 'Offer';
              };
              const getDesc = (text) => {
                const c = text.indexOf(':');
                return (c > 0 && c < 25) ? text.slice(c + 1).trim() : text.trim();
              };
              const cols = lines.length === 1 ? 'grid-cols-1' : lines.length === 2 ? 'grid-cols-2' : 'grid-cols-3';
              return (
                <div className="border border-slate-200 rounded-lg mb-2 overflow-hidden">
                  <button
                    onClick={() => setOffersExpanded(v => !v)}
                    className="w-full flex items-center justify-between gap-2 px-2.5 py-2 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full border-2 border-slate-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-bold text-slate-700 leading-none">%</span>
                      </div>
                      <span className="text-sm font-bold text-slate-900">Offers</span>
                      <span className="text-xs text-slate-400">({lines.length})</span>
                    </div>
                    {offersExpanded ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                  </button>
                  {offersExpanded && (
                    <div className={`grid divide-x divide-slate-100 border-t border-slate-100 ${cols}`}>
                      {lines.map((offer, i) => (
                        <div key={i} className="p-2">
                          <p className="text-xs font-bold text-slate-900 mb-0.5">{getTitle(offer, i)}</p>
                          <p className="text-xs text-slate-500 leading-snug line-clamp-2 mb-1">{getDesc(offer)}</p>
                          <button
                            onClick={() => setOpenOfferPopover({ title: getTitle(offer, i), full: offer })}
                            className="text-xs text-blue-600 font-medium hover:underline"
                            data-testid={`offer-detail-btn-${i}`}
                          >1 offer ›</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <hr className="border-slate-200 mb-2" />

            {/* Anchor for "select a variant" prompt from Add to Cart / Buy Now */}
            <div ref={variantPickerRef} className="relative">
              {variantHint && (
                <div
                  role="alert"
                  data-testid="variant-required-hint"
                  className="absolute left-1/2 -translate-x-1/2 -top-1 z-30 whitespace-nowrap bg-slate-900 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-lg"
                >
                  {(product.variant_options || []).length ? "Please select at least one variant" : "Please select a quantity"}
                </div>
              )}
            </div>

            {/* Color Selection — image-only swatches, always scrollable */}
            {colorValues.length > 0 && (
              <div className="mb-2">
                {/* Selected color name shown next to the label (was removed by
                    mistake in bca92d4; ticket 291868). */}
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1" data-testid="selected-color-label">
                  color{galleryColor && (
                    <>: <span className="normal-case tracking-normal text-sm font-semibold text-slate-800">{galleryColor}</span></>
                  )}
                </p>
                <div className="flex gap-2 flex-nowrap overflow-x-auto no-scrollbar pb-1" data-testid={PRODUCT_PAGE.colorSelect}>
                  {colorValues.map(color => {
                    const combo = product.variant_combinations?.find(c => c.color === color && c.image);
                    const variantThumb = combo?.image;
                    const isActive = galleryColor === color;
                    const thisColorQty = allVariants
                      .filter(v => v.color === color)
                      .reduce((sum, v) => sum + (quantities[`${color}|${v.size || ""}`] || 0), 0);
                    return (
                      <button
                        key={color}
                        onClick={() => { setGalleryColor(color); setActiveImg(0); }}
                        title={color}
                        className={`relative flex-shrink-0 w-12 h-12 rounded-lg border-2 transition-all ${
                          isActive
                            ? "border-orange-500 shadow-sm"
                            : "border-slate-200 hover:border-orange-300"
                        }`}
                      >
                        <div className="w-full h-full rounded-md overflow-hidden">
                          {variantThumb ? (
                            <img src={variantThumb} alt={color} className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                          ) : (
                            <div className="w-full h-full bg-slate-100" style={{ background: COLOR_MAP[color] || "#e2e8f0" }} />
                          )}
                        </div>
                        {thisColorQty > 0 && (
                          <div className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 shadow z-10">
                            {thisColorQty}
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Quantity / Size Selection — smart: single variant → QTY picker, multi → collapsible table */}
            {(() => {
              const COLLAPSE_LIMIT = 2;
              const filteredVariants = allVariants.length > 0
                ? (galleryColor ? allVariants.filter(v => v.color === galleryColor) : allVariants)
                : [];

              // Single-variant mode: 1 (or 0) combo after color filter, OR 0-1 sizeValues
              const isSingleMode = (
                (allVariants.length > 0 && filteredVariants.length <= 1) ||
                (allVariants.length === 0 && sizeValues.length <= 1)
              );

              if (isSingleMode) {
                const singleKey = filteredVariants.length === 1
                  ? `${filteredVariants[0].color || ""}|${filteredVariants[0].size || ""}`
                  : sizeValues.length === 1 ? `|${sizeValues[0]}` : "qty";
                const qty = quantities[singleKey] || 0;
                return (
                  <div className="mb-4" data-testid={PRODUCT_PAGE.sizeSelect}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">qty</p>
                      {qty > 0 && (
                        <span className="text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full">
                          {qty} selected
                        </span>
                      )}
                    </div>
                    <div className="border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-4">
                      <button
                        onClick={() => handleQtyChange(singleKey, qty - 1)}
                        disabled={qty === 0}
                        className="w-9 h-9 border border-slate-300 rounded-lg flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="flex-1 text-center text-xl font-bold tabular-nums text-slate-900" data-testid={PRODUCT_PAGE.quantityInput}>
                        {qty}
                      </span>
                      <button
                        onClick={() => handleQtyChange(singleKey, qty + 1)}
                        className="w-9 h-9 border border-slate-300 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {qty > 0 && (
                      <div className="mt-2 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
                        <span className="text-sm text-slate-600">
                          {qty} item{qty !== 1 ? "s" : ""} &middot; <span className="font-semibold text-orange-600">&#8377;{currentPrice}/unit</span>
                        </span>
                        <span className="text-sm font-bold text-slate-900">
                          Total: &#8377;{(qty * Number(currentPrice)).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                );
              }

              // Multi-variant combinations (allVariants > 1 after color filter)
              if (allVariants.length > 0) {
                const COLLAPSE_LIMIT = 2;
                const visibleVariants = variantsExpanded ? filteredVariants : filteredVariants.slice(0, COLLAPSE_LIMIT);
                const hiddenCount = filteredVariants.length - COLLAPSE_LIMIT;
                return (
                  <div className="mb-4" data-testid={PRODUCT_PAGE.sizeSelect}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">size</p>
                      {totalQty > 0 && (
                        <span className="text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full">
                          {totalQty} total selected
                        </span>
                      )}
                    </div>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      {visibleVariants.map((combo) => {
                        const key = `${combo.color || ""}|${combo.size || ""}`;
                        const comboQty = quantities[key] || 0;
                        return (
                          <div
                            key={key}
                            className={`flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-100 transition-colors ${comboQty > 0 ? "bg-orange-50" : "hover:bg-slate-50"}`}
                          >
                            <span className="text-sm text-slate-800 flex-1">
                              {combo.size || ""}
                              {combo.size_label ? ` (${combo.size_label})` : ""}
                            </span>
                            {combo.stock > 0 && (
                              <span className="text-xs text-slate-400 flex-shrink-0">{combo.stock} in stock</span>
                            )}
                            {variantStockMode(product, combo) === "backorder" && typeof combo.stock === "number" && (combo.stock <= 0 || comboQty > combo.stock) && (
                              <span className="text-[11px] text-amber-600 flex-shrink-0" data-testid="variant-dispatch">{dispatchTexts.outOfStock}</span>
                            )}
                            {variantStockMode(product, combo) === "strict" && typeof combo.stock === "number" && combo.stock <= 0 && (
                              <span className="text-[11px] text-red-500 flex-shrink-0">Out of stock</span>
                            )}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button onClick={() => handleQtyChange(key, comboQty - 1)} disabled={comboQty === 0} className="w-7 h-7 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="w-7 text-center text-sm font-semibold tabular-nums" data-testid={PRODUCT_PAGE.quantityInput}>{comboQty}</span>
                              <button onClick={() => handleQtyChange(key, comboQty + 1)} className="w-7 h-7 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 transition-colors">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {filteredVariants.length > COLLAPSE_LIMIT && (
                      <button
                        onClick={() => setVariantsExpanded(v => !v)}
                        className="mt-1.5 w-full flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        {variantsExpanded ? <><ChevronUp className="w-3.5 h-3.5" /> Show fewer sizes</> : <><ChevronDown className="w-3.5 h-3.5" /> +{hiddenCount} more sizes</>}
                      </button>
                    )}
                    {stockNote && <p className="mt-1.5 text-xs text-red-500" data-testid="stock-note">{stockNote}</p>}
                    {totalQty > 0 && (
                      <div className="mt-2 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
                        <span className="text-sm text-slate-600">{totalQty} item{totalQty !== 1 ? "s" : ""} &middot; <span className="font-semibold text-orange-600">&#8377;{currentPrice}/unit</span></span>
                        <span className="text-sm font-bold text-slate-900">Total: &#8377;{(totalQty * Number(currentPrice)).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                );
              }

              // Multi sizeValues only (no variant_combinations)
              if (sizeValues.length > 1) {
                const COLLAPSE_LIMIT = 2;
                const visibleSizes = variantsExpanded ? sizeValues : sizeValues.slice(0, COLLAPSE_LIMIT);
                const hiddenCount = sizeValues.length - COLLAPSE_LIMIT;
                return (
                  <div className="mb-4" data-testid={PRODUCT_PAGE.sizeSelect}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-slate-400 uppercase tracking-wide">size</p>
                      {totalQty > 0 && (
                        <span className="text-xs bg-orange-100 text-orange-700 font-semibold px-2 py-0.5 rounded-full">
                          {totalQty} total selected
                        </span>
                      )}
                    </div>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      {visibleSizes.map((size, idx) => {
                        const key = `|${size}`;
                        const comboQty = quantities[key] || 0;
                        return (
                          <div key={idx} className={`flex items-center gap-3 px-4 py-3 border-b last:border-0 border-slate-100 transition-colors ${comboQty > 0 ? "bg-orange-50" : "hover:bg-slate-50"}`}>
                            <span className="text-sm text-slate-800 flex-1">{size}</span>
                            <div className="flex items-center gap-2">
                              <button onClick={() => handleQtyChange(key, comboQty - 1)} disabled={comboQty === 0} className="w-7 h-7 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed">
                                <Minus className="w-3.5 h-3.5" />
                              </button>
                              <span className="w-7 text-center text-sm font-semibold" data-testid={PRODUCT_PAGE.quantityInput}>{comboQty}</span>
                              <button onClick={() => handleQtyChange(key, comboQty + 1)} className="w-7 h-7 border border-slate-300 rounded flex items-center justify-center hover:bg-slate-100">
                                <Plus className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {sizeValues.length > COLLAPSE_LIMIT && (
                      <button
                        onClick={() => setVariantsExpanded(v => !v)}
                        className="mt-1.5 w-full flex items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        {variantsExpanded ? <><ChevronUp className="w-3.5 h-3.5" /> Show fewer sizes</> : <><ChevronDown className="w-3.5 h-3.5" /> +{hiddenCount} more sizes</>}
                      </button>
                    )}
                    {totalQty > 0 && (
                      <div className="mt-2 px-3 py-2.5 bg-orange-50 border border-orange-200 rounded-lg flex items-center justify-between">
                        <span className="text-sm text-slate-600">{totalQty} item{totalQty !== 1 ? "s" : ""} &middot; <span className="font-semibold text-orange-600">&#8377;{currentPrice}/unit</span></span>
                        <span className="text-sm font-bold text-slate-900">Total: &#8377;{(totalQty * Number(currentPrice)).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                );
              }

              return null;
            })()}

            {/* Buttons — inline, compact */}
            <hr className="border-slate-200 mb-2" />
            <div ref={inlineButtonsRef} className="flex gap-2 mb-2" data-testid="inline-cta-buttons">
              <button
                data-testid={PRODUCT_PAGE.addToCartButton}
                onClick={() => handleCta("/cart")}
                className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 rounded-lg text-sm flex items-center justify-center gap-1.5 transition-all"
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                Add to Cart
              </button>
              <button
                data-testid={PRODUCT_PAGE.buyNowButton}
                onClick={() => handleCta("/checkout")}
                className="flex-1 bg-slate-900 hover:bg-slate-800 text-white font-semibold py-2 rounded-lg text-sm transition-all"
              >
                Buy Now
              </button>
            </div>

            {/* In Stock + Delivery info — below CTA buttons */}
            {(() => {
              const now = new Date();
              const rawDays = product.shipping_returns?.deliver_within;
              const deliveryDays = rawDays ? (parseInt(String(rawDays)) || settingsDeliveryDays) : settingsDeliveryDays;
              const delivery = new Date(now);
              delivery.setDate(delivery.getDate() + deliveryDays);
              const dayName = delivery.toLocaleDateString("en-IN", { weekday: "long" });
              const dayDate = delivery.toLocaleDateString("en-IN", { day: "numeric", month: "long" });
              const cutoff = new Date(now);
              cutoff.setHours(settingsCutoffHour, 0, 0, 0);
              const diffMs = cutoff - now;
              const diffHrs = Math.max(0, Math.floor(diffMs / 3600000));
              const diffMins = Math.max(0, Math.floor((diffMs % 3600000) / 60000));
              return (
                <div className="mb-2">
                  {editingLocation ? (
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-slate-800">Delivery by <span className="font-bold">{dayName}, {dayDate}.</span></span>
                      <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <input autoFocus type="text" value={locationInput}
                        onChange={e => setLocationInput(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter" && locationInput.trim()) { const val = locationInput.trim(); setDeliverTo(val); localStorage.setItem("it_deliver_to", val); setEditingLocation(false); }
                          if (e.key === "Escape") setEditingLocation(false);
                        }}
                        placeholder="Pincode or city…"
                        className="border border-slate-300 rounded-lg px-2 py-1 w-36 focus:outline-none focus:border-orange-500"
                        data-testid="location-input"
                      />
                      <button onClick={() => { const val = locationInput.trim(); if (val) { setDeliverTo(val); localStorage.setItem("it_deliver_to", val); } setEditingLocation(false); }} className="font-semibold text-white bg-orange-500 hover:bg-orange-600 px-3 py-1 rounded-lg" data-testid="location-confirm-btn">Apply</button>
                      <button onClick={() => setEditingLocation(false)} className="text-slate-500 hover:text-slate-700">Cancel</button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm flex-wrap">
                      <span className="text-slate-800">Delivery by <span className="font-bold">{dayName}, {dayDate}.</span></span>
                      <span className="text-slate-300">|</span>
                      <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                      <span className="text-slate-700">Deliver to <span className="font-medium">{deliverTo || "India"}</span></span>
                      <button onClick={() => { setLocationInput(deliverTo); setEditingLocation(true); }} className="text-blue-600 hover:underline" data-testid="location-change-btn">Change</button>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Trust Badges — global defaults from settings, per-product override */}
            {(() => {
              const returnDays = storeSettings?.return_window_days || "10";
              const HARDCODED_DEFAULTS = [
                { icon: "truck",     label: "Free\nDelivery",                         enabled: true },
                { icon: "rotateccw", label: `${returnDays} days Return\n& Exchange`,  enabled: true },
                { icon: "package",   label: "Brand\nDelivered",                       enabled: true },
                { icon: "lock",      label: "Secure\nTransaction",                    enabled: true },
              ];
              const ICON_MAP = {
                truck:       Truck,
                rotateccw:   RotateCcw,
                lock:        Lock,
                shield:      Shield,
                award:       Award,
                package:     Package,
                star:        Star,
                zap:         Zap,
                checkcircle: CheckCircle,
                xcircle:     XCircle,
                clock:       Clock,
                gift:        Gift,
              };
              const isNonReturnable = product.return_policy === "Non-returnable";
              const usingPerProduct = product.trust_badges?.length > 0;

              // Priority: per-product → global settings (with dynamic return label) → hardcoded defaults
              const globalDefaults = (storeSettings?.default_trust_badges?.length
                ? storeSettings.default_trust_badges
                : HARDCODED_DEFAULTS
              ).map(b =>
                // Dynamically update the return badge label using the configured return window
                b.icon === "rotateccw" ? { ...b, label: `${returnDays} days Return\n& Exchange` } : b
              );

              let badges = (usingPerProduct ? product.trust_badges : globalDefaults)
                .filter(b => b.enabled !== false);

              // Return badge: replace with Non-Returnable badge if product is non-returnable
              if (isNonReturnable && !usingPerProduct) {
                badges = badges.map(b =>
                  b.icon === "rotateccw"
                    ? { icon: "xcircle", label: "Non-Returnable\nItem", enabled: true, _nonReturn: true }
                    : b
                );
              }

              if (!badges.length) return null;
              return (
                <>
                  <hr className="border-slate-200 mb-2" />
                  <div className="pt-1 pb-1">
                    <div className="flex items-start justify-around gap-1">
                      {badges.map((badge, i) => {
                        const IconComp = ICON_MAP[badge.icon] || Shield;
                        const isNonReturnBadge = badge._nonReturn;
                        return (
                          <div key={i} className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${isNonReturnBadge ? "bg-red-50" : "bg-slate-100"}`}>
                              <IconComp className={`w-5 h-5 ${isNonReturnBadge ? "text-red-500" : "text-slate-600"}`} strokeWidth={1.5} />
                            </div>
                            <span className={`text-[10px] font-medium text-center leading-tight whitespace-pre-line ${isNonReturnBadge ? "text-red-600" : "text-blue-900"}`}>{badge.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Product Details — Amazon style, below trust badges */}
            {(product.specifications?.length > 0 || product.product_features?.length > 0 || product.product_description) && (
              <div className="mt-3 pt-3 border-t border-slate-200">
                <h2 className="text-base font-bold text-slate-900 mb-2">Product details</h2>
                <hr className="border-slate-200 mb-3" />

                {/* Top highlights — specs key-value table */}
                {product.specifications?.length > 0 && (
                  <div className="mb-4">
                    <div>
                      {product.specifications.slice(0, 7).map((spec, idx) => (
                        <div key={idx} className="flex py-0.5">
                          <span className="text-sm font-bold text-slate-900 w-2/5 flex-shrink-0">{spec.name}</span>
                          <span className="text-sm text-slate-700">{spec.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <hr className="border-slate-200 mb-3" />

                {/* About this item */}
                {product.product_features?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-base font-bold text-slate-900 mb-3">About this item</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      {(aboutExpanded ? product.product_features : product.product_features.slice(0, 3)).map((f, i) => (
                        <li key={i} className="text-sm text-slate-700 leading-snug">{f}</li>
                      ))}
                    </ul>
                    {product.product_features.length > 3 && (
                      <button
                        onClick={() => setAboutExpanded(v => !v)}
                        className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                      >
                        {aboutExpanded
                          ? <><ChevronUp className="w-3.5 h-3.5" /> See less</>
                          : <><ChevronDown className="w-3.5 h-3.5" /> See more</>
                        }
                      </button>
                    )}
                  </div>
                )}
                {product.product_description && !product.product_features?.length && (
                  <div className="mb-3">
                    <h3 className="text-base font-bold text-slate-900 mb-3">About this item</h3>
                    <div
                      className={`raw-html-content text-sm text-slate-700 leading-relaxed overflow-hidden transition-all ${aboutExpanded ? "" : "line-clamp-6"}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.product_description) }}
                    />
                    <button onClick={() => setAboutExpanded(v => !v)} className="mt-2 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700">
                      {aboutExpanded ? <><ChevronUp className="w-3.5 h-3.5" /> See less</> : <><ChevronDown className="w-3.5 h-3.5" /> See more</>}
                    </button>
                  </div>
                )}

                {/* Measurements — only shown when values exist, collapsed by default */}
                {product.measurements?.length > 0 && (
                  <>
                    <hr className="border-slate-200 mb-0" />
                    <button onClick={() => setMeasurementsOpen(v => !v)} className="w-full flex items-center justify-between py-4">
                      <h3 className="text-xl font-extrabold text-slate-900">Measurements</h3>
                      {measurementsOpen ? <ChevronUp className="w-5 h-5 text-slate-700" /> : <ChevronDown className="w-5 h-5 text-slate-700" />}
                    </button>
                    {measurementsOpen && (
                      <div className="mb-2">
                        <hr className="border-slate-200 mb-3" />
                        {product.measurements.map((item, idx) => (
                          <div key={idx}>
                            <div className="flex py-3">
                              <span className="text-sm font-bold text-slate-900 w-2/5 flex-shrink-0">{item.name}</span>
                              <span className="text-sm text-slate-800">{item.value}</span>
                            </div>
                            <hr className="border-slate-200" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Style — only shown when values exist, collapsed by default */}
                {product.style_details?.length > 0 && (
                  <>
                    <hr className="border-slate-200 mb-0" />
                    <button onClick={() => setStyleOpen(v => !v)} className="w-full flex items-center justify-between py-4">
                      <h3 className="text-xl font-extrabold text-slate-900">Style</h3>
                      {styleOpen ? <ChevronUp className="w-5 h-5 text-slate-700" /> : <ChevronDown className="w-5 h-5 text-slate-700" />}
                    </button>
                    {styleOpen && (
                      <div className="mb-2">
                        <hr className="border-slate-200 mb-3" />
                        {product.style_details.map((item, idx) => (
                          <div key={idx}>
                            <div className="flex py-3">
                              <span className="text-sm font-bold text-slate-900 w-2/5 flex-shrink-0">{item.name}</span>
                              <span className="text-sm text-slate-800">{item.value}</span>
                            </div>
                            <hr className="border-slate-200" />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Q&A */}
                {product.qa_items?.length > 0 && (
                  <>
                    <button onClick={() => setQaOpen(v => !v)} className="w-full flex items-center justify-between py-4">
                      <h3 className="text-xl font-extrabold text-slate-900">Q &amp; A</h3>
                      {qaOpen ? <ChevronUp className="w-5 h-5 text-slate-700" /> : <ChevronDown className="w-5 h-5 text-slate-700" />}
                    </button>
                    {qaOpen && (
                      <div className="mb-2">
                        <hr className="border-slate-200 mb-3" />
                        <div className="space-y-3">
                          {product.qa_items.map((item, idx) => (
                            <div key={idx} className="bg-slate-50 rounded-lg p-3">
                              <p className="text-sm font-bold text-slate-900 mb-1">Q: {item.question}</p>
                              <p className="text-sm text-slate-700">A: {item.answer}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <hr className="border-slate-200 mb-4" />
                  </>
                )}

                {/* See all specifications — desktop only */}
                <div className="hidden lg:block">
                  <button
                    onClick={() => setShowSpecsPopover(true)}
                    className="border border-slate-800 text-slate-900 text-sm px-5 py-2 rounded-full font-medium hover:bg-slate-50 transition-colors"
                    data-testid="see-all-specs-btn"
                  >
                    See all product specifications
                  </button>
                </div>
              </div>
            )}

            {/* Feature icons now live in the left gallery column */}
          </div>

          {/* Right: Bulk Buying + Marketplace + Special Offer — 2 cols ≈ 17% */}
          <div className="lg:col-span-2 lg:sticky lg:top-28 lg:self-start">

            {/* ── BULK BUYING — desktop right column only ──────────── */}
            {sortedTiers.length > 0 && (
              <>
                {/* Mobile AliExpress card handled in center column above price */}

                {/* Desktop: vertical table in right column */}
                <div className="hidden lg:block border border-slate-200 rounded-lg overflow-hidden mb-2">
                  <div className="px-2.5 py-1.5 border-b border-slate-100">
                    <p className="text-xs font-bold text-slate-900">Bulk Buying (Save More)</p>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-100">
                        <th className="px-2.5 py-1 text-left text-[10px] font-bold text-slate-600">Quantity</th>
                        <th className="px-2.5 py-1 text-right text-[10px] font-bold text-slate-600">Price (&#8377;)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTiers.map((tier, idx) => {
                        const tierMin = Number(tier.min_qty);
                        const nextTier = sortedTiers[idx + 1];
                        const tierMax = nextTier ? Number(nextTier.min_qty) - 1 : Infinity;
                        const isTierActive = totalQty > 0 && totalQty >= tierMin && totalQty <= tierMax;
                        return (
                          <tr key={idx} className={`border-b last:border-0 border-slate-100 ${isTierActive ? "bg-orange-50" : idx % 2 === 0 ? "bg-white" : "bg-blue-50/40"}`}>
                            <td className="px-2.5 py-1 text-xs text-slate-700">
                              <div className="flex items-center gap-1">
                                {isTierActive && <div className="w-1 h-1 rounded-full bg-orange-500 flex-shrink-0" />}
                                {tier.min_qty}{nextTier ? ` - ${Number(nextTier.min_qty) - 1}` : "+"}
                              </div>
                            </td>
                            <td className={`px-2.5 py-1 text-right text-xs font-bold ${isTierActive ? "text-orange-600" : "text-slate-900"}`}>
                              &#8377;{tier.price}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="px-2.5 py-2 space-y-1.5">
                    <button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold py-1.5 rounded-lg transition-all">
                      Get Best Price
                    </button>
                    <button className="w-full border border-slate-300 text-slate-700 text-xs font-medium py-1.5 rounded-lg hover:bg-slate-50 transition-all flex items-center justify-center gap-1"
                      onClick={handleGetProductData}
                      disabled={pdState === "adding"}
                      data-testid="get-product-data-btn"
                    >
                      {pdState === "adding" ? (
                        <><span className="w-3 h-3 border-2 border-slate-400 border-t-orange-500 rounded-full animate-spin" />Adding…</>
                      ) : pdState === "added" ? (
                        <><CheckCircle className="w-3 h-3 text-green-500" />View Product Data</>
                      ) : (
                        <><Download className="w-3 h-3" />Get Product Data</>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── MARKETPLACE SP — desktop right column only ───────── */}
            {mpPrices.length > 0 && (
              <div className="hidden lg:block border border-slate-200 rounded-lg overflow-hidden mb-2">
                {/* Header — same style as Offers */}
                <button
                  onClick={() => setMarketplaceExpanded(v => !v)}
                  className="w-full flex items-center justify-between px-2.5 py-2 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full border-2 border-slate-700 flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-slate-700 leading-none">SP</span>
                    </div>
                    <span className="text-xs font-bold text-slate-900">Marketplace Suggestive SP</span>
                    <span className="text-xs text-slate-400">({mpPrices.length})</span>
                  </div>
                  {marketplaceExpanded
                    ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    : <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                </button>

                {/* Vertical list rows — desktop only */}
                {marketplaceExpanded && (
                  <div className="border-t border-slate-100 divide-y divide-slate-50">
                    {mpPrices.map((mp, mpIdx) => (
                      <div key={mp.id} className="relative">
                        <div className="flex items-center gap-2 px-2.5 py-2">
                          <MarketplaceIcon id={mp.id} size={28} />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-800 capitalize leading-tight">{mp.label}</p>
                            <p className="text-xs font-bold text-slate-900 leading-tight">&#8377;{mp.price.toLocaleString()}</p>
                            {mp.profit !== 0 && (
                              <p className={`text-[10px] font-semibold leading-tight ${mp.profit > 0 ? "text-green-600" : "text-red-500"}`}>
                                {mp.profit > 0 ? "+" : ""}&#8377;{Math.round(Math.abs(mp.profit))} profit
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => setOpenMpPopover(openMpPopover === mp.id ? null : mp.id)}
                            className={`flex-shrink-0 text-[11px] font-semibold px-2 py-1 rounded-lg border transition-all ${
                              openMpPopover === mp.id
                                ? "border-blue-400 bg-blue-50 text-blue-600"
                                : "border-slate-200 text-blue-600 hover:border-blue-300 hover:bg-blue-50"
                            }`}
                          >
                            View →
                          </button>
                        </div>
                        {openMpPopover === mp.id && (
                          <MarketplacePopover
                            id={mp.id}
                            sp={sp}
                            profit={mp.profit}
                            onClose={() => setOpenMpPopover(null)}
                            openUpward={mpIdx >= mpPrices.length - 2}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── SPECIAL OFFER ──────────────────────────────────────── */}
            {/* Special Offer */}
            {(product.special_offer_code || product.special_offer_label) && (
              <div className="border-2 border-dashed border-red-300 bg-red-50 rounded-xl p-3 mb-3">
                {/* Row 1: "Special Offer  Ends in  HH:MM:SS" */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="text-sm font-bold text-red-600">Special Offer</span>
                  <span className="text-xs text-slate-500">Ends in</span>
                  <CountdownTimer hours={product.special_offer_timer_hours || 24} />
                </div>
                {/* Offer label */}
                {product.special_offer_label && (
                  <p className="text-sm text-slate-800 mb-2 leading-snug">{product.special_offer_label}</p>
                )}
                {/* Coupon code row */}
                {product.special_offer_code && (
                  <div className="flex items-center justify-between bg-white border border-red-200 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Heart className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      <span className="text-xs text-slate-600">
                        Use Code:{" "}
                        <strong className="font-mono tracking-widest text-slate-900">{product.special_offer_code}</strong>
                      </span>
                    </div>
                    <button
                      onClick={() => navigator.clipboard?.writeText(product.special_offer_code)}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700 ml-3 flex-shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Delivery info and Trust Badges moved to center column */}
          </div>
        </div>
      </div>

      {/* Product Content Creatives — from Content tab (product_description rich text) */}
      {product.product_description && (
        <div
          className="raw-html-content mt-8"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(product.product_description) }}
        />
      )}

      {/* ── Flipkart-style Ratings & Reviews Section ──────────────────── */}
      {(product.num_reviews || 0) > 0 && (() => {
        const reviews  = generateReviews(product, 6);
        const rating   = product.star_rating || 4.5;
        const total    = product.num_reviews || 0;
        const label    = rating >= 4.5 ? "Excellent" : rating >= 4 ? "Very Good" : rating >= 3.5 ? "Good" : "Average";
        const labelColor = rating >= 4 ? "#388e3c" : rating >= 3.5 ? "#ff9f00" : "#ff6161";
        const isClothing = /underwear|brief|boxer|shirt|top|dress|kurti|saree|wear|cloth/i.test((product.item_name||"").toLowerCase());
        const isJewelry  = /ring|necklace|bracelet|earring|bangle|jewel|pendant/i.test((product.item_name||"").toLowerCase());
        const ATTRS = isClothing
          ? ["Quality","Comfort","Fit","Value for Money"]
          : isJewelry
          ? ["Quality","Design","Value for Money","Durability"]
          : ["Quality","Value for Money","Design","Durability"];
        const attrRatings = ATTRS.map((_, i) => Math.max(3.2, Math.min(4.9, rating - 0.2 + (i % 3) * 0.1 - (i % 2) * 0.15)).toFixed(1));

        const allPhotos = [
          ...(product.main_image ? [product.main_image] : []),
          ...(product.gallery_images || []).filter(Boolean),
          ...(product.variant_combinations || []).map(v => v.image).filter(Boolean),
        ].filter((v, i, a) => a.indexOf(v) === i).slice(0, 8);

        const extraPhotos = Math.max(0, total - 5);

        return (
          <div ref={reviewsRef} className="max-w-[1800px] mx-auto px-3 mt-10 mb-10 scroll-mt-28">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">

              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h2 className="text-base font-bold text-slate-900">Ratings &amp; Reviews</h2>
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </div>

              <div className="px-5 py-5">
                {/* Rating summary row */}
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white font-bold text-lg" style={{ background: labelColor }}>
                    {rating.toFixed(1)} <span className="text-base">★</span>
                  </div>
                  <div>
                    <p className="text-base font-bold text-slate-900">{label}</p>
                    <p className="text-xs text-slate-500">
                      based on {total.toLocaleString()} ratings by{" "}
                      <span className="text-green-700 font-semibold">✓ Verified Buyers</span>
                    </p>
                  </div>
                </div>

                {/* Photo mosaic */}
                {allPhotos.length >= 2 && (
                  <div className="flex gap-1 mb-5 rounded-lg overflow-hidden" style={{ height: 240 }}>
                    {/* Big left photo */}
                    <div className="w-1/2 flex-shrink-0 overflow-hidden bg-slate-100">
                      <img src={allPhotos[0]} alt="Review photo" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.background="#f1f5f9"; }} />
                    </div>
                    {/* 2×2 right grid */}
                    <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-1">
                      {[1,2,3,4].map((n, i) => {
                        const img = allPhotos[n];
                        const isLast = i === 3;
                        return (
                          <div key={i} className="relative overflow-hidden bg-slate-100">
                            {img && <img src={img} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.background="#f1f5f9"; }} />}
                            {isLast && extraPhotos > 0 && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center cursor-pointer" onClick={() => navigate(`/reviews/${product.url_slug}`)}>
                                <span className="text-white font-bold text-xl">+{extraPhotos}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Category attribute ratings */}
                <div className="flex gap-4 overflow-x-auto no-scrollbar pb-1 mb-5 border-b border-slate-100">
                  {ATTRS.map((attr, i) => (
                    <div key={attr} className="flex-shrink-0 flex items-center gap-1.5 text-sm text-slate-700 pb-3">
                      <span className="font-medium">{attr}</span>
                      <span className="text-orange-500 font-bold">{attrRatings[i]}★</span>
                      {i < ATTRS.length - 1 && <span className="text-slate-200 ml-2">|</span>}
                    </div>
                  ))}
                </div>

                {/* Horizontal scrollable review cards */}
                <div className="relative">
                  <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="review-cards-row">
                    {reviews.map(rv => (
                      <div key={rv.id} className="flex-shrink-0 w-72 border border-slate-200 rounded-xl p-3.5 bg-slate-50">
                        {/* Stars */}
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-white text-xs font-bold" style={{ background: rv.stars >= 4 ? "#388e3c" : rv.stars === 3 ? "#ff9f00" : "#ff6161" }}>
                            {rv.stars} ★
                          </div>
                          <span className="text-xs font-semibold text-slate-800 truncate">{rv.title}</span>
                        </div>
                        {/* Body (truncated) */}
                        <p className="text-xs text-slate-600 leading-relaxed mb-2 line-clamp-3">
                          {rv.body}
                          <span className="text-blue-600 cursor-pointer"> ...more</span>
                        </p>
                        {/* Review image if any */}
                        {rv.images[0] && (
                          <div className="w-12 h-12 rounded overflow-hidden bg-slate-100 mb-2">
                            <img src={rv.images[0]} alt="" className="w-full h-full object-cover" onError={e => { e.target.parentElement.style.display="none"; }} />
                          </div>
                        )}
                        {/* Name + time */}
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-semibold text-slate-700">{rv.name}</span>
                          <span className="text-[10px] text-slate-400">{rv.monthYear}</span>
                        </div>
                        {/* Verified badge */}
                        {rv.verified && (
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-[10px] text-green-700 font-semibold">✓ Certified Buyer</span>
                          </div>
                        )}
                        {/* Helpful */}
                        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-slate-200">
                          <button className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700">
                            <ThumbsUp className="w-3 h-3" /> {rv.helpful}
                          </button>
                          <button className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-700">
                            <ThumbsDown className="w-3 h-3" /> {rv.thumbsDown}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Show all reviews */}
                <div className="mt-4">
                  <button
                    onClick={() => navigate(`/reviews/${product.url_slug}`)}
                    className="w-full py-3 border border-slate-300 rounded-lg text-sm text-blue-600 hover:bg-slate-50 transition-colors font-semibold flex items-center justify-center gap-1"
                    data-testid="show-all-reviews-btn"
                  >
                    Show all {total.toLocaleString()} reviews <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Related Products */}

      {/* Related Products */}
      {product.related_products_data?.length > 0 && (
        <div className="max-w-[1800px] mx-auto px-3 mt-8">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Related Products</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {product.related_products_data.map(rp => (
              <Link
                key={rp.id}
                to={`/products/${rp.url_slug || rp.id}`}
                className="group border border-slate-200 rounded-xl overflow-hidden bg-white hover:shadow-md transition-all"
              >
                <div className="aspect-square bg-slate-50 overflow-hidden">
                  {rp.main_image ? (
                    <img
                      src={rp.main_image}
                      alt={rp.item_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-8 h-8 text-slate-300" />
                    </div>
                  )}
                </div>
                <div className="p-2">
                  <p className="text-xs text-slate-700 font-medium line-clamp-2 leading-snug">{rp.item_name}</p>
                  {rp.deal_price_incl_gst > 0 && (
                    <p className="text-sm font-bold text-slate-900 mt-1">₹{rp.deal_price_incl_gst}</p>
                  )}
                  {rp.star_rating > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <span className="text-orange-400 text-xs">★</span>
                      <span className="text-xs text-slate-500">{rp.star_rating.toFixed(1)}</span>
                      {rp.num_reviews > 0 && <span className="text-xs text-slate-400">({rp.num_reviews})</span>}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Shop by Price — shown after Related Products */}
      {shopByPriceConfig && (
        <div className="mt-8" data-testid="product-shop-by-price">
          <ShopByPriceSection config={shopByPriceConfig} />
        </div>
      )}

      <SiteFooter />
      {/* Offer Popover Modal */}
      {openOfferPopover && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center"
          onClick={() => setOpenOfferPopover(null)}
          data-testid="offer-popover-overlay"
        >
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-md mx-0 sm:mx-4 shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
            data-testid="offer-popover-card"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Tag className="w-4 h-4 text-orange-600" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{openOfferPopover.title}</h3>
              </div>
              <button
                onClick={() => setOpenOfferPopover(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors flex-shrink-0"
                data-testid="offer-popover-close"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            {/* Body */}
            <div className="px-5 py-4">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                {openOfferPopover.full}
              </p>
              <p className="text-xs text-slate-400 mt-4 pt-3 border-t border-slate-100">
                *Terms & conditions apply. Offer subject to availability.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sticky CTA Bar */}
      {stickyEnabled && showStickyBar && (
        <div
          className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] animate-in slide-in-from-bottom-2 duration-200"
          data-testid="sticky-cta-bar"
        >
          <div className="max-w-[1800px] mx-auto px-3 py-2.5 flex items-center gap-2">
            {totalQty > 0 && (
              <div className="flex-shrink-0 hidden sm:flex flex-col leading-tight mr-2">
                <span className="text-xs text-slate-500">{totalQty} item{totalQty !== 1 ? "s" : ""}</span>
                <span className="text-sm font-bold text-slate-900">&#8377;{(totalQty * Number(currentPrice)).toLocaleString()}</span>
              </div>
            )}
            <button
              onClick={() => handleCta("/cart")}
              className="flex-1 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-semibold py-2.5 rounded-lg text-sm flex items-center justify-center gap-2 transition-all shadow-sm"
              data-testid="sticky-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </button>
            <button
              onClick={() => handleCta("/checkout")}
              className="flex-1 bg-slate-900 hover:bg-slate-800 active:bg-slate-700 text-white font-semibold py-2.5 rounded-lg text-sm transition-all"
              data-testid="sticky-buy-now"
            >
              Buy Now
            </button>
          </div>
        </div>
      )}

      {/* ── Specs Panel Overlay — desktop only ─────────────────────── */}
      {showSpecsPopover && (
        <div className="fixed inset-0 z-[60] hidden lg:flex">
          {/* Left dim backdrop — 1/3 of screen, click to close */}
          <div
            className="w-1/3 bg-black/50 relative cursor-pointer"
            onClick={() => setShowSpecsPopover(false)}
          >
            <button
              onClick={e => { e.stopPropagation(); setShowSpecsPopover(false); }}
              className="absolute top-5 left-5 w-9 h-9 bg-white rounded-full flex items-center justify-center shadow-lg hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4 text-slate-700" />
            </button>
          </div>

          {/* Right white panel — 2/3 of screen */}
          <div className="w-2/3 bg-white flex flex-col shadow-2xl">
            {/* Sticky tabs header */}
            <div className="flex items-center gap-2 px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10 flex-wrap">
              {[
                ...(product.measurements?.length > 0 ? [{ id: "measurements", label: "Measurements" }] : []),
                ...(product.style_details?.length > 0 ? [{ id: "style", label: "Style" }] : []),
                ...(product.specifications?.length > 0 ? [{ id: "specifications", label: "Item details" }] : []),
                ...(product.product_features?.filter(Boolean).length > 0 ? [{ id: "features", label: "Features & Specs" }] : []),
                ...(product.qa_items?.length > 0 ? [{ id: "qa", label: "Q & A" }] : []),
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveSpecTab(tab.id);
                    document.getElementById(`spec-section-${tab.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all ${
                    activeSpecTab === tab.id
                      ? "border-blue-600 text-blue-700 bg-blue-50 font-semibold"
                      : "border-slate-300 text-slate-700 hover:border-slate-500 bg-white"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Scrollable content */}
            <div className="flex-1 overflow-y-auto px-8 py-6 space-y-10">
              {product.measurements?.length > 0 && (
                <div id="spec-section-measurements">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Measurements</h2>
                  <div className="divide-y divide-slate-200">
                    {product.measurements.map((m, i) => (
                      <div key={i} className="flex py-3">
                        <span className="w-[45%] text-sm font-semibold text-slate-800 pr-4">{m.name}</span>
                        <span className="w-[55%] text-sm text-slate-700">{m.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.style_details?.length > 0 && (
                <div id="spec-section-style">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Style</h2>
                  <div className="divide-y divide-slate-200">
                    {product.style_details.map((s, i) => (
                      <div key={i} className="flex py-3">
                        <span className="w-[45%] text-sm font-semibold text-slate-800 pr-4">{s.name}</span>
                        <span className="w-[55%] text-sm text-slate-700">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.specifications?.length > 0 && (
                <div id="spec-section-specifications">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Item details</h2>
                  <div className="divide-y divide-slate-200">
                    {product.specifications.map((s, i) => (
                      <div key={i} className="flex py-3">
                        <span className="w-[45%] text-sm font-semibold text-slate-800 pr-4">{s.name}</span>
                        <span className="w-[55%] text-sm text-slate-700">{s.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.product_features?.filter(Boolean).length > 0 && (
                <div id="spec-section-features">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Features &amp; Specs</h2>
                  <div className="divide-y divide-slate-200">
                    {product.product_features.filter(Boolean).map((f, i) => (
                      <div key={i} className="py-3">
                        <span className="text-sm text-slate-700">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {product.qa_items?.length > 0 && (
                <div id="spec-section-qa">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Q &amp; A</h2>
                  <div className="space-y-3">
                    {product.qa_items.map((item, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-4">
                        <p className="text-sm font-bold text-slate-900 mb-1.5">Q: {item.question}</p>
                        <p className="text-sm text-slate-700 leading-relaxed">A: {item.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!product.measurements?.length && !product.style_details?.length &&
               !product.specifications?.length && !product.product_features?.filter(Boolean).length &&
               !product.qa_items?.length && (
                <div className="text-center py-16 text-slate-400 text-sm">
                  No specification data available for this product.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Plan Enrollment Modal – opened when Get Product Data hits the free limit */}
      {showPlanModal && (
        <PlanEnrollModal
          token={customerToken}
          onClose={() => setShowPlanModal(false)}
          onSuccess={() => { setShowPlanModal(false); navigate("/account", { state: { tab: "my-plans" } }); }}
        />
      )}
    </div>
  );
}
